import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Col, Form, Input, Row, Select } from "antd";
import UnitOfMeasureType from "../../enums/UnitOfMeasureType";
import DimensionUnitType from "../../enums/DimensionUnitType";
import WeightUnitType from "../../enums/WeightUnitType";
import { validateEqualToOrGreaterThanZero, validateGreaterThanZero } from "../../../../swzutils/ValidationUtil";
import { userHasPermission } from "../../../../swzutils/UserPermissionsUtil";
import { isNumber } from "../../../../swzutils";
import { UnitOfMeasureEnum, WeightUnitEnum } from "../../api/Representation";
import { ConsignmentFreightRequestTransformer } from "./api/Representation";
import ProductSelect from "./ProductSelect";
import { useAppSelector } from "../../../../store";
import { loggedInUserSelector } from "../../../../store/slice/auth";
const ConsignmentFreightItemForm = ({ tenantId, editMode = 'EDIT_NONE', freightItem, canChangeSavedProduct = false, showTrackingCode = true, showWasteRecordId = true, showDescription = true, }, exportedRef) => {
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [weightUnit, setWeightUnit] = useState(WeightUnitEnum.KG);
    const [showDimension, setShowDimension] = useState(false);
    const [showGrossAndTare, setShowGrossAndTare] = useState(false);
    const [form] = Form.useForm();
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const dimensionsRequired = userHasPermission(loggedInUser, ['mandate:consignmentFreightDimensions']);
    useImperativeHandle(exportedRef, () => ({
        validateFields: () => {
            return new Promise((resolve, reject) => {
                form.validateFields().then(formValues => {
                    const request = ConsignmentFreightRequestTransformer.fromResponse(formValues);
                    resolve({
                        ...request,
                        version: freightItem?.version,
                        productReference: selectedProduct?.reference,
                        productDescription: selectedProduct?.description
                    });
                }).catch(reject);
            });
        },
        resetFields: () => {
            setSelectedProduct(null);
            setWeightUnit(WeightUnitEnum.KG);
            setShowDimension(false);
            setShowGrossAndTare(false);
            form.resetFields();
        }
    }));
    useEffect(() => {
        if (freightItem) {
            form.setFieldsValue(freightItem);
        }
    }, [freightItem]);
    useEffect(() => {
        onUOMChanged(freightItem?.netUnitOfMeasure || freightItem?.unitOfMeasure);
    }, [freightItem?.unitOfMeasure, freightItem?.netUnitOfMeasure]);
    useEffect(() => {
        if (freightItem) {
            setWeightUnit(freightItem.weightUnit);
        }
    }, [freightItem?.weightUnit]);
    const onUOMSelectChanged = (uom) => {
        const netUnitOfMeasure = form.getFieldValue("netUnitOfMeasure");
        if (!netUnitOfMeasure) {
            onUOMChanged(uom);
        }
    };
    const onUOMChanged = (uom) => {
        const uomType = UnitOfMeasureType.valueOf(uom);
        if (uomType) {
            setShowDimension(uomType.hasDimensions());
            setShowGrossAndTare(uomType.hasGrossTare());
        }
        else {
            setShowDimension(false);
            setShowGrossAndTare(false);
        }
        calcTotalWeight();
    };
    const onProductSelected = (product) => {
        setSelectedProduct(product);
        if (product) {
            // Only apply product defaults if they are set
            if (product.unitOfMeasure) {
                form.setFieldsValue({
                    "unitOfMeasure": product.unitOfMeasure
                });
                onUOMChanged(product.unitOfMeasure);
            }
            if (product.dimensionUnit) {
                form.setFieldsValue({
                    "height": product.height,
                    "width": product.width,
                    "length": product.length,
                    "dimensionUnit": product.dimensionUnit
                });
            }
            if (product.weightUnit) {
                form.setFieldsValue({
                    "weightUnit": product.weightUnit,
                    "weight": product.weight
                });
            }
        }
        else {
            onUOMChanged(null);
        }
    };
    const calcTotalWeight = () => {
        const formItem = form.getFieldsValue();
        const orderedQuantity = formItem.orderedQuantity || freightItem?.orderedQuantity;
        const weight = formItem.weight || freightItem?.weight;
        if (orderedQuantity && isNumber(orderedQuantity) && weight && isNumber(weight)) {
            const totalWeight = Math.round(orderedQuantity * weight * 100) / 100;
            form.setFieldsValue({
                "totalWeight": totalWeight
            });
        }
        setWeightUnit(formItem.weightUnit);
    };
    const editNet = editMode === 'EDIT_NET' || editMode === 'EDIT_NET_WASTE' || editMode === 'EDIT_NET_DIMENSIONS_WEIGHT_WASTE';
    const editWasteRecordId = editMode === 'EDIT_ALL' || editMode === 'EDIT_ALL_EXCEPT_UOM' || editMode === 'EDIT_NET_WASTE' || editMode === 'EDIT_NET_DIMENSIONS_WEIGHT_WASTE';
    const editDimensions = editMode === 'EDIT_ALL' || editMode === 'EDIT_ALL_EXCEPT_UOM' || editMode === 'EDIT_NET_DIMENSIONS_WEIGHT_WASTE';
    const editWeight = editMode === 'EDIT_ALL' || editMode === 'EDIT_ALL_EXCEPT_UOM' || editMode === 'EDIT_NET_DIMENSIONS_WEIGHT_WASTE';
    const showNetQtyUOM = editNet || freightItem?.netUnitOfMeasure || freightItem?.netQuantity;
    const readOnly = editMode !== 'EDIT_ALL' && editMode !== 'EDIT_ALL_EXCEPT_UOM';
    return (_jsxs(Form, { layout: "vertical", form: form, children: [showTrackingCode &&
                _jsx(Form.Item, { name: "trackingCode", label: "Tracking Code", children: _jsx(Input, { readOnly: readOnly, placeholder: "Tracking Code" }) }), showWasteRecordId &&
                _jsx(Form.Item, { name: "wasteRecordId", label: "EPA Waste Record Id", children: _jsx(Input, { readOnly: !editWasteRecordId, placeholder: "EPA Waste Record Id" }) }), _jsx(Form.Item, { name: "productId", label: "Product", children: _jsx(ProductSelect, { tenantId: tenantId, 
                    // Can only edit the description on new freight items
                    // to change the freight product the item should be removed and then added
                    disabled: (!!freightItem?.id && !canChangeSavedProduct), onProductSelected: onProductSelected }) }), showDescription &&
                _jsx(Form.Item, { name: "description", label: "Additional Details", children: _jsx(Input, { readOnly: readOnly, placeholder: "Additional Details" }) }), _jsxs(Row, { gutter: 5, children: [_jsx(Col, { xs: 8, children: _jsx(Form.Item, { name: "orderedQuantity", label: "Quantity", rules: [
                                { required: true, message: 'Quantity is required' },
                                { validator: validateEqualToOrGreaterThanZero }
                            ], children: _jsx(Input, { readOnly: readOnly, placeholder: "Quantity", onChange: calcTotalWeight }) }) }), _jsx(Col, { xs: 16, children: _jsx(Form.Item, { name: "unitOfMeasure", label: "Unit of Measure", rules: [
                                { required: true, message: 'Unit of measure is required' }
                            ], children: _jsx(Select, { onChange: onUOMSelectChanged, allowClear: true, defaultActiveFirstOption: false, placeholder: "Ordered Unit of measure", disabled: readOnly || editMode === 'EDIT_ALL_EXCEPT_UOM', showSearch: {
                                    optionFilterProp: ['label']
                                }, options: UnitOfMeasureType.consignmentFreightSelectOptionsArray() }) }) })] }), showNetQtyUOM &&
                _jsxs(Row, { gutter: 5, children: [_jsx(Col, { xs: 8, children: _jsx(Form.Item, { name: "netQuantity", label: "Net Quantity", rules: [
                                    { validator: validateEqualToOrGreaterThanZero }
                                ], children: _jsx(Input, {}) }) }), _jsx(Col, { xs: 16, children: _jsx(Form.Item, { name: "netUnitOfMeasure", label: "Net Unit of Measure", children: _jsx(Select, { onChange: onUOMChanged, allowClear: true, defaultActiveFirstOption: false, placeholder: "Net Unit of measure", showSearch: {
                                        optionFilterProp: ['label']
                                    }, options: UnitOfMeasureType.consignmentFreightSelectOptionsArray() }) }) })] }), showDimension &&
                _jsxs(Row, { gutter: 5, children: [_jsx(Col, { xs: 6, children: _jsx(Form.Item, { name: "length", label: "Length", rules: [
                                    { required: dimensionsRequired, message: 'Length is required' },
                                    { validator: validateGreaterThanZero }
                                ], children: _jsx(Input, { readOnly: !editDimensions }) }) }), _jsx(Col, { xs: 6, children: _jsx(Form.Item, { name: "width", label: "Width", rules: [
                                    { required: dimensionsRequired, message: 'Width is required' },
                                    { validator: validateGreaterThanZero }
                                ], children: _jsx(Input, { readOnly: !editDimensions }) }) }), _jsx(Col, { xs: 6, children: _jsx(Form.Item, { name: "height", label: "Height", rules: [
                                    { required: dimensionsRequired, message: 'Height is required' },
                                    { validator: validateGreaterThanZero }
                                ], children: _jsx(Input, { readOnly: !editDimensions }) }) }), _jsx(Col, { xs: 6, children: _jsx(Form.Item, { name: "dimensionUnit", label: "Units", children: _jsx(Select, { disabled: !editDimensions, allowClear: true, options: DimensionUnitType.selectOptionsArray(true) }) }) })] }), showDimension &&
                _jsxs(Row, { gutter: 5, children: [_jsx(Col, { xs: 8, children: _jsx(Form.Item, { name: "weight", label: "Weight", rules: [
                                    { validator: validateGreaterThanZero }
                                ], children: _jsx(Input, { readOnly: !editWeight, onChange: calcTotalWeight }) }) }), _jsx(Col, { xs: 8, children: _jsx(Form.Item, { name: "weightUnit", label: "Weight Unit", children: _jsx(Select, { disabled: !editWeight, allowClear: true, onChange: calcTotalWeight, options: WeightUnitType.selectOptionsArray(true) }) }) }), _jsx(Col, { xs: 8, children: _jsx(Form.Item, { name: "totalWeight", label: "Total Weight", children: _jsx(Input, { readOnly: true, className: "read-only", suffix: weightUnit }) }) })] }), showDimension &&
                _jsx(Row, { gutter: 5, children: _jsx(Col, { xs: 24, children: _jsxs("p", { children: [_jsx("strong", { children: "NOTE:" }), " Dimensions and weight are specified for each individual item quantity.", _jsx("br", {}), "Dimensions and weight are mandatory for deliveries placed on the marketplace."] }) }) }), showGrossAndTare && editNet &&
                _jsxs(Row, { gutter: 5, children: [_jsx(Col, { xs: 8, children: _jsx(Form.Item, { name: "grossQuantity", label: "Gross", rules: [
                                    { validator: validateGreaterThanZero }
                                ], children: _jsx(Input, {}) }) }), _jsx(Col, { xs: 8, children: _jsx(Form.Item, { name: "tareQuantity", label: "Tare", rules: [
                                    { validator: validateGreaterThanZero }
                                ], children: _jsx(Input, {}) }) })] })] }));
};
export default React.forwardRef(ConsignmentFreightItemForm);
